import { Engine } from 'engine/Engine';
import { Entity } from 'engine/Entity';
import { Transform } from 'engine/Transform';
import { ColliderFacet, ColliderType } from 'engine/facets/ColliderFacet';
import { Facet } from 'engine/facets/Facet';
import { StaticSpriteFacet } from 'engine/facets/StaticSpriteFacet';
import { UIFacet } from 'engine/facets/UIFacet';
import { AudioManager } from 'engine/lib/AudioManager';
import { addV2 } from 'engine/lib/V2';
import { create } from 'engine/lib/create';
import { Activatable } from 'game/behaviors/Activatable';
import { DeathScreen } from 'game/components/DeathScreen';
import { MainWorldSprite } from 'game/sprites/MainWorldSprite';
import { TrailSprite } from 'game/sprites/TrailSprite';
import { BotState } from 'game/state/BotState';
import { RootState } from 'game/state/RootState';
import noop from 'lodash/noop';
import { createElement } from 'react';
import { BotEntity } from '../BotEntity';
import { PlayerEntity } from '../PlayerEntity';
import { TreeEntity } from '../TreeEntity';
import { FranklinDialog, FranklinEndDialog } from '../tent-int/FranklinDialog';
import { createEndMap } from './EndMap';
import { MainWorldUI } from './MainWorldUI';
import { createOverworldEntities } from './OverworldMap';
import { Teleporter } from './Teleporter';
import { createTent } from './TentMap';
import { DialogMode } from './dialog/DialogMode';

const botClosePath = require('game/sfx/bot-close.mp3');
const botOpenPath = require('game/sfx/bot-open.mp3');
const bounceSrc = require('game/sfx/bounce.mp3');
const smartDoorSrc = require('game/sfx/smart-door.mp3');
const zipperCloseSrc = require('game/sfx/zipper-close.mp3');
const zipperOpenSrc = require('game/sfx/zipper-open.mp3');

const bounce = new AudioManager(bounceSrc);
const smartDoor = new AudioManager(smartDoorSrc, { volume: 0.2 });
const zipperClose = new AudioManager(zipperCloseSrc, { volume: 0.2 });
const zipperOpen = new AudioManager(zipperOpenSrc, { volume: 0.25 });

interface MainWorldProps {
  rootState: RootState,
  reload: () => void,
}

export class MainWorldBehavior extends Facet<MainWorldProps> {

  ui: MainWorldUI | null = null;
  player: PlayerEntity | null = null;
  editingBot: Entity<{ state: BotState }> | null = null;
  reactivate: Activatable[] = [];

  botOpenSound = new AudioManager(botOpenPath, { volume: 0.5 });
  botCloseSound = new AudioManager(botClosePath, { volume: 0.5 });

  deathTime = -1;
  deathDuration = 100;

  endingCreated = false;

  onPlayerDeath = (engine: Engine) => {
    engine.time.timeScale = 0.12;
    this.deathTime = engine.time.now;
    const { reload, rootState } = this.props;
    this.entity.addFacet(create(UIFacet, {
      Component: () => createElement(DeathScreen, {
        reload,
        playerState: rootState.player,
      }),
    }), engine);
  };

  onExitEditBot = (engine: Engine) => {
    // Defer enabling bots' Activatables until after they update
    // to prevent them from re-opening in the same tick.
    engine.queuePostUpdate(() => {
      this.reactivate.forEach(a => a.disable(false));
      this.reactivate.splice(0, this.reactivate.length);
    });

    this.editingBot = null;
    this.botCloseSound.play();
  };

  disableWhileEditing(entity: Entity, engine: Engine) {
    const activatable = entity.getFacet(Activatable);
    if (!activatable) return;
    engine.queuePostUpdate(() => {
      activatable.disable(true);
      this.reactivate.push(activatable);
    });
  }

  start(engine: Engine) {
    const { rootState } = this.props;

    engine.camera.scale = 0.5;
    engine.camera.position = addV2([0, -80], rootState.player.position);

    const player = engine.add(create(PlayerEntity, {
      playerState: rootState.player,
      onDeath: this.onPlayerDeath,
    }));
    this.player = player;
    this.ui = engine.add(create(MainWorldUI, { bot: null, onExitEditBot: () => this.onExitEditBot(engine) }));

    const editBot = (botEntity: Entity<{ state: BotState }>) => {
      this.editingBot = botEntity;
      this.disableWhileEditing(botEntity, engine);
      this.botOpenSound.play();
    };
    rootState.bots.forEach(bot => {
      const botEntity: Entity<{ state: BotState }> = engine.add(create(BotEntity, {
        state: bot,
        repair: this.player?.repair || noop,
        editBot: () => editBot(botEntity),
      }));
    });

    rootState.trees.forEach(treeState => {
      engine.add(
        create(TreeEntity, { treeState }),
        { transform: new Transform({ position: treeState.position }) },
      );
    });

    const teleporter = this.entity.addFacet(create(Teleporter, {}), engine);
    const dialog = this.entity.addFacet(create(DialogMode, {}), engine);
    const overworldEntities = createOverworldEntities({
      onEnterTent: () => {
        zipperClose.play();
        teleporter.teleport(player, [283, -4328], engine);
      },
      onEnterLab: () => {
        smartDoor.play();
        teleporter.teleport(player, [11467, 5045], engine);
        if (!this.endingCreated) {
          this.endingCreated = true;
          const end = createEndMap({
            offset: [11440, 5000],
            onExit: () => {
              teleporter.teleport(player, [8181, 2414], engine);
            },
            onTalk: () => {
              end.franklin.entity.getFacet(Activatable)?.activate(false, engine);
              dialog.enter(FranklinEndDialog, engine);
              bounce.play();
            },
          });
          Object.values(end).forEach(({ entity, config }) => engine.add(entity, config));
        }
      },
    });
    overworldEntities.door.entity.props.state = rootState.labDoor;
    overworldEntities.panel.entity.props.onHack = () => {
      this.disableWhileEditing(overworldEntities.panel.entity, engine);
      editBot(overworldEntities.door.entity);
    };
    Object.values(overworldEntities).forEach(({ entity, config }) => engine.add(entity, config));

    const tentEntities = createTent({
      offset: [0, -5000],
      onExit: () => {
        zipperOpen.play();
        teleporter.teleport(player, [1978, 1321], engine);
      },
      onTalk: () => {
        tentEntities.franklin.entity.getFacet(Activatable)?.activate(false, engine);
        dialog.enter(FranklinDialog, engine);
        bounce.play();
      },
    });
    Object.values(tentEntities).forEach(({ entity, config }) => engine.add(entity, config));
  }

  update(delta: number, engine: Engine): void {
    const { camera, input } = engine;
    camera.position = addV2([0, -80], this.props.rootState.player.position);

    if (this.editingBot && (input.keyDown.Escape || input.keyDown.KeyE)) {
      this.onExitEditBot(engine);
    }

    if (this.ui) this.ui.props.bot = this.editingBot;
  }
}

export class MainWorld extends Entity<MainWorldProps> {
  zIndex = -1;
  createFacets(props: MainWorldProps) {
    return [
      create(MainWorldBehavior, props),
      create(StaticSpriteFacet, { frame: MainWorldSprite }),
      create(StaticSpriteFacet, { frame: TrailSprite, offset: [1822, 1709] }),

      create(ColliderFacet, { type: ColliderType.Box, size: [10440, 64], offset: [0, -64] }),
      create(ColliderFacet, { type: ColliderType.Box, size: [10440, 64], offset: [0, 6000] }),
      create(ColliderFacet, { type: ColliderType.Box, size: [64, 6000], offset: [10440, 0] }),
      create(ColliderFacet, { type: ColliderType.Box, size: [64, 6000], offset: [-64, 0] }),
    ];
  }
}
